import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core'
import moment from 'moment'
import * as R from 'ramda'
import { range } from 'lodash'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import { epochToViewUTC, selectAllRentals, selectOnce, selectRentalEntities } from '@tokeet-frontend/tv3-platform'
import * as fromRoot from '@tv3/store/state'
import { isSomething } from '@tokeet-frontend/tv3-platform'
import { CurrencyPipe } from '@angular/common'
import { Inquiry, LoadInquiriesFilter } from '@tv3/store/inquiry/inquiry.model'
import { SaveForm } from '@tokeet-frontend/tv3-platform'
import { epochToView } from '@tokeet-frontend/tv3-platform'
import { observeOn } from 'rxjs/operators'
import { asyncScheduler } from 'rxjs'
import { downloadCSV } from '@tv3/utils/functions/download'
import { BookingCostResolver } from '@tokeet/cost-resolver'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import Papa from 'papaparse'

declare const window: any

@Component({
  selector: 'app-export-inquiries-report-dialog',
  templateUrl: './export-inquiries-report-dialog.component.html',
  host: { class: 'modal-content' },
  styleUrls: ['./export-inquiries-report-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ExportInquiriesReportDialogComponent implements OnInit {
  isExporting = false

  years: string[]
  months = moment.months()

  form = this.fb.group({
    year: ['', [Validators.required]],
    month: ['', [Validators.required]],
    rentals: ['', [Validators.required]],
    onlyBooked: [false],
  })

  exportableData: any[]

  columns = [
    $localize`Rental Name`,
    $localize`Guest Name`,
    $localize`Arrive`,
    $localize`Depart`,
    $localize`Guests`,
    $localize`Nights`,
    $localize`Initial Price`,
    $localize`Taxes`,
    $localize`Discounts & Fee`,
    $localize`Final Price`,
    $localize`Formula Cost`,
    $localize`Booking Source`,
    $localize`Booking Date`,
    $localize`Referral ID`,
  ]

  rentals$ = this.store.pipe(observeOn(asyncScheduler), select(selectAllRentals))

  constructor(
    private store: Store<fromRoot.State>,
    public dialogRef: MatDialogRef<ExportInquiriesReportDialogComponent>,
    public fb: UntypedFormBuilder,
    private inquiryService: InquiryService
  ) {}

  ngOnInit() {
    this.years = R.map((i) => moment().subtract(i, 'year').format('YYYY'), range(3, -3, -1))
  }

  close() {
    this.dialogRef.close()
  }

  @SaveForm()
  async download(form: UntypedFormGroup) {
    this.isExporting = true
    await this.doExport()
    this.isExporting = false
    this.dialogRef.close()
  }

  private async doExport() {
    const filters = this.form.getRawValue()
    const month = moment.utc().year(filters.year).month(filters.month)
    const params: LoadInquiriesFilter = {
      rentals: filters.rentals,
      period: {
        from: month.clone().startOf('month').unix(),
        to: month.clone().endOf('month').unix(),
      },
    }

    if (filters.onlyBooked) {
      params.status = 'booked'
    }
    const rentalsById = await this.store.pipe(selectOnce(selectRentalEntities)).toPromise()
    const items = await this.inquiryService.getBy(params).toPromise()

    const ids = R.map((t) => t.id, items).filter((t) => !!t)

    const formulaCostsByIds = await this.inquiryService.getCostAfterFormulaBatch(ids).toPromise()

    this.exportableData = R.map((e: Inquiry) => {
      const charges = BookingCostResolver.parseBookingCharges(e as any)
      const rental = rentalsById[e.rentalId]

      const currencyCode = R.pathOr('USD', ['currency', 'code'], rental)
      const charge = R.pathOr(0, ['charge'], charges)
      const taxFee = R.pathOr(0, ['taxFee'], charges)
      const feeSum = R.pathOr(0, ['feeSum'], charges)
      const discountSum = R.pathOr(0, ['discountSum'], charges)
      const sum = R.pathOr(0, ['sum'], charges)
      const formulaCost = formulaCostsByIds[e.id]

      return {
        [$localize`Rental Name`]: rental.name || '',
        [$localize`Guest Name`]: e.guestNameView,
        [$localize`Arrive`]: epochToViewUTC(e.guestArrive, 'MM/DD/YYYY'),
        [$localize`Depart`]: epochToViewUTC(e.guestDepart, 'MM/DD/YYYY'),
        [$localize`Guests`]: e.guestsView,
        [$localize`Nights`]: e.nightsView,
        [$localize`Initial Price`]: new CurrencyPipe('en-US').transform(charge, currencyCode),
        [$localize`Taxes`]: new CurrencyPipe('en-US').transform(taxFee, currencyCode),
        [$localize`Discounts & Fee`]: new CurrencyPipe('en-US').transform(feeSum + discountSum, currencyCode),
        [$localize`Final Price`]: new CurrencyPipe('en-US').transform(sum, currencyCode),
        [$localize`Formula Cost`]:
          formulaCost >= 0 ? new CurrencyPipe('en-US').transform(formulaCost, currencyCode) : '',
        [$localize`Booking Source`]: R.pathOr('', ['inquirySource'], e),
        [$localize`Booking Date`]: epochToView(e.booked, 'MM/DD/YYYY'),
        // TV4-6226: affiliate / referral id captured from the booking link
        [$localize`Referral ID`]: R.pathOr('', ['attributes', 'referral', 'id'], e),
      }
    }, items)

    const csvString = Papa.unparse({
      fields: this.columns,
      data: JSON.stringify(this.exportableData),
    })
    const fileName = `inquiries_report_${this.form.get('year').value}_${this.form.get('month').value}_${
      this.form.get('onlyBooked').value ? 'booked' : 'all'
    }.csv`
    downloadCSV(csvString, fileName)
  }
}
